import React, { useEffect, useState } from 'react';
import { DollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { bapi, fmtDate, usd } from '@/lib/billingApi';
import { odownload } from '@/lib/officialsApi';
import { StatusPill } from '@/components/billing/StatusPill';
import { EMPTY_FILTERS, TransactionFilters } from './TransactionFilters';
import { TransactionDrawer } from './TransactionDrawer';
import { GatewayTag, KIND_LABEL, PRODUCT_SHORT, TestTag, Th, statusText } from './shared';

const Row = ({ p, onOpen }) => (
  <tr onClick={() => onOpen(p.id)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`txn-row-${p.id}`}>
    <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{fmtDate(p.paid_at || p.created_at)}</td>
    <td className="px-3 py-2.5 max-w-[240px]">
      <div className="font-semibold text-slate-800 truncate">{p.user_email || '—'}</div>
      {p.user_name && <div className="text-xs text-slate-500 truncate">{p.user_name}</div>}
    </td>
    <td className="px-3 py-2.5 whitespace-nowrap">
      <div className="text-slate-800">{PRODUCT_SHORT[p.product_id] || p.product_name}</div>
      <div className="text-xs text-slate-500">{KIND_LABEL[p.kind] || p.kind}</div>
    </td>
    <td className="px-3 py-2.5 whitespace-nowrap"><GatewayTag gateway={p.gateway} /> <span className="text-xs text-slate-500 ml-1">{p.method}</span></td>
    <td className="px-3 py-2.5 text-right font-mono tabular-nums whitespace-nowrap">
      {usd(p.total)}{p.refunded_amount > 0 && <div className="text-xs text-violet-700">−{usd(p.refunded_amount)}</div>}
    </td>
    <td className="px-3 py-2.5 whitespace-nowrap"><StatusPill status={p.status} label={statusText(p.status)} testId={`txn-status-${p.id}`} />{p.simulated && <TestTag />}</td>
    <td className="px-3 py-2.5 whitespace-nowrap text-xs font-mono text-slate-500">{p.receipt_number || '—'}</td>
  </tr>
);

export const TransactionsTable = ({ onChanged }) => {
  const [f, setF] = useState(EMPTY_FILTERS);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      bapi('/admin/billing/transactions', { query: { ...f, limit: 200 } }).then(setData).catch((e) => toast.error(e.message));
    }, f.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [f, tick]);

  const exportCsv = () => odownload('/admin/billing/transactions/export.csv', f, `schedule-guru-transactions-${new Date().toISOString().slice(0, 10)}.csv`)
    .then(() => toast.success('CSV exported')).catch((e) => toast.error(e.message));
  const changed = () => { setTick((x) => x + 1); onChanged(); };

  return (
    <Card className="border-slate-200">
      <CardHeader className="space-y-4">
        <CardTitle className="text-lg flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-700" />Transactions ({(data?.total ?? 0).toLocaleString()})</CardTitle>
        <TransactionFilters f={f} setF={setF} onExport={exportCsv} />
      </CardHeader>
      <CardContent className="p-0">
        {!data ? <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00247D]" /></div>
          : !data.rows.length ? <p className="p-8 text-center text-slate-500 text-sm" data-testid="sales-empty">No transactions match those filters.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-y border-slate-200"><tr>
                  <Th>Date</Th><Th>Customer</Th><Th>Product</Th><Th>Method</Th><Th right>Amount</Th><Th>Status</Th><Th>Receipt</Th>
                </tr></thead>
                <tbody data-testid="sales-rows">{data.rows.map((p) => <Row key={p.id} p={p} onOpen={setOpenId} />)}</tbody>
              </table>
            </div>
          )}
      </CardContent>
      <TransactionDrawer id={openId} onClose={() => setOpenId(null)} onChanged={changed} />
    </Card>
  );
};
